"use client"
import { NewsletterSectionDto } from "api/generated/MazdaInterfaces"
import usePrivacyPolicyLink from "hooks/usePrivacyPolicyLink"
import { useInView } from "react-intersection-observer"
import styled from "styled-components"
import { HomePageContainer } from "theme/shared"
import NewsletterForm from "./NewsletterForm"

const Band = styled.div`
  width: 100%;
  background-color: ${(p) => p.theme.colors.newsletterBg};
  border-top: 1px solid ${(p) => p.theme.colors.newsletterBorderMobile};
  border-bottom: 1px solid ${(p) => p.theme.colors.newsletterBorderMobile};

  ${(p) => p.theme.breakpoints.up("md")} {
    border-top: 2px solid ${(p) => p.theme.colors.newsletterBg};
    border-bottom: 2px solid ${(p) => p.theme.colors.newsletterBg};
  }
`

const Inner = styled(HomePageContainer)`
  padding-top: 34px;
  padding-bottom: 49.5px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding-top: 64px;
    padding-bottom: 64px;
  }

  ${(p) => p.theme.breakpoints.up("xl")} {
    padding-top: 63.59px;
    padding-bottom: 66px;
  }
`

const Grid = styled.div<{ $inView: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr;
    align-items: center;
    transition: all 0.9s cubic-bezier(0.19, 0.88, 0.53, 1.12);
    transition-delay: 0.3s;
    opacity: ${({ $inView }) => ($inView ? 1 : 0)};
    transform: ${({ $inView }) =>
      $inView ? "translate3d(0, 0, 0)" : "translate3d(0, -40px, 0)"};
  }
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`

const Eyebrow = styled.p`
  margin: 0;
  font-family: MazdaType, OENaPro, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  color: ${(p) => p.theme.colors.newPrimaryRed};

  ${(p) => p.theme.breakpoints.up("md")} {
    font-weight: 700;
    letter-spacing: 6px;
    line-height: 24px;
  }
`

const Headline = styled.h2`
  margin: 0;
  font-family: OENaPro, sans-serif;
  font-weight: 300;
  font-size: 32px;
  line-height: 40px;
  color: ${(p) => p.theme.colors.black};

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 48px;
    line-height: 48px;
  }
`

const Paragraph = styled.p`
  margin: 2px 0 0;
  max-width: 420px;
  font-family: OENaPro, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.neutral};

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const Consent = styled.p`
  margin: 0;
  width: 100%;
  font-family: OENaPro, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  text-align: right;
  color: ${(p) => p.theme.colors.neutral};

  a {
    color: ${(p) => p.theme.colors.newsletterLink};
    font-weight: 700;
    text-decoration: underline;
  }
`

const Newsletter = ({
  newsLetterData,
}: {
  newsLetterData: NewsletterSectionDto
}) => {
  const { ref, inView } = useInView({ triggerOnce: true })
  const privacyPolicyLink = usePrivacyPolicyLink()

  return (
    <Band ref={ref}>
      <Inner>
        <Grid $inView={inView}>
          <Copy>
            {newsLetterData.sectionLabel && (
              <Eyebrow>{newsLetterData.sectionLabel}</Eyebrow>
            )}
            <Headline dir="auto">{newsLetterData.sectionHeadline}</Headline>
            <Paragraph dir="auto">
              {newsLetterData.sectionDescription}
            </Paragraph>
          </Copy>

          <FormColumn>
            <NewsletterForm />
            <Consent dir="auto">
              {`הזנת הפרטים מהווה הסכמה לקבל מקבוצת דלק מוטורס דיוורים, עדכונים, מבצעים, אירועים והטבות מטעם המותג MAZDA באמצעי התקשרות אותם הזנת. הפרטים יישמרו במאגר המידע ויעשה בהם שימוש בהתאם לתנאי `}
              <a href={privacyPolicyLink} target="_blank" rel="noreferrer">
                מדיניות הפרטיות
              </a>
              {` של הקבוצה`}
            </Consent>
          </FormColumn>
        </Grid>
      </Inner>
    </Band>
  )
}

export default Newsletter
